import api from './api';

/**
 * Calendar Redesign (Task 3C) — typed fetcher for the unified calendar feed
 * (`GET /api/v1/calendar/events`), behind FEATURE flag
 * `unified_calendar_feed_enabled`. Replaces the legacy triple-fetch (appointments
 * calendar + travels calendar + blockouts) with a single server-merged source of
 * truth. Follows the conventions of appointments-api.ts / travel-api.ts (shared
 * `api` axios client, `{ data }` unwrap, interceptor-driven auth + 401 handling).
 *
 * The contract is FIXED (backend is being built to it exactly); code against it.
 */

/** A single event `type` as it arrives on the wire (arrival/departure split out). */
export type FeedEventType =
  | 'clinic_visit'
  | 'lead_call'
  | 'internal'
  | 'travel_arrival'
  | 'travel_departure'
  | 'transfer'
  | 'blockout'
  // EKDent 260906 — a recorded clinical visit. Emitted ONLY on a
  // patient-scoped feed (`leadId`), never on the clinic-wide board.
  | 'visit';

/** Provenance discriminator — selects the click-action handler + carries the raw id. */
export type FeedEventSourceType = 'appointment' | 'travel' | 'transfer' | 'blockout' | 'clinical_visit';

/**
 * `types[]` filter values accepted by the endpoint. NOTE the asymmetry with
 * {@link FeedEventType}: the filter collapses arrival+departure into a single
 * `travel` bucket (the server expands it back to both event types).
 * `transfer` (Logistics driver rides) is silently dropped by the server unless
 * the clinic runs logistics AND the caller holds logistics.view.
 */
export type CalendarFeedFilterType =
  | 'clinic_visit'
  | 'lead_call'
  | 'internal'
  | 'travel'
  | 'transfer'
  | 'blockout';

/** Lead payload — mirrors the old appointments/travels feeds. */
export interface FeedEventLead {
  id: string;
  full_name: string;
  phone: string;
  has_medical_alert?: boolean;
  medical_alert?: string | null;
}

export interface FeedEventUser {
  id: number;
  full_name: string;
  /**
   * Per-doctor chip colour, resolved server-side (DoctorColorResolver). Null
   * when appointment_presets.calendar.doctor_colors is off for the clinic, and null when
   * the appointment is booked against someone who is not an active doctor —
   * both cases mean "fall back to the category/status fill", never "grey".
   */
  color?: string | null;
}

export interface FeedEventRoom {
  id: string;
  name: string;
  color: string;
  capacity: number;
}

export interface FeedEvent {
  /** Composite id: "appointment:<uuid>" | "travel:arrival-<id>" | "travel:departure-<id>" | "blockout:<id>". */
  id: string;
  source: { type: FeedEventSourceType; id: string };
  type: FeedEventType;
  title: string;
  /** ISO 8601 with timezone. */
  start: string;
  /** ISO 8601 with timezone. */
  end: string;
  /**
   * true for a travel leg WITHOUT a flight time and a transfer WITHOUT a pickup
   * time (date at 00:00 — no 09:00 pin from the server). A timed leg/ride is a
   * 30-minute display window at its wall-clock time on the clinic calendar.
   */
  all_day: boolean;
  status: string | null;
  subject: { lead: FeedEventLead | null } | null;
  resources: {
    user: FeedEventUser | null;
    room: FeedEventRoom | null;
    /** ArtDent 261005 — appointment events only, and only where the clinic books co-doctors. */
    co_doctors?: { id: number; full_name: string }[];
  };
  display: { color_token: string; icon: string; badges: string[] };
  /**
   * Source-specific bag. appointment: category, duration_minutes, confirmation /
   * check-in, clinical visit, type_source. travel: travel_id, direction, airport,
   * airline, hotel, travel_status, time ('HH:MM' on a timed leg, else null). blockout: label/reason, source.
   */
  meta: Record<string, unknown>;
}

export interface CalendarFeedMeta {
  count: number;
  enabled_types: string[];
  legend: { type: string; color_token: string }[];
}

export interface CalendarFeedResponse {
  data: FeedEvent[];
  meta: CalendarFeedMeta;
}

export interface CalendarFeedParams {
  /** ISO 8601 range start. */
  start: string;
  /** ISO 8601 range end. */
  end: string;
  /** Optional server-side type filter (arrival+departure collapse into `travel`). */
  types?: CalendarFeedFilterType[];
  userId?: string;
  roomId?: string;
  category?: string;
  /**
   * EKDent 260906 — narrow the whole feed to ONE patient, for the calendar on
   * their record. Clinic-wide blockouts are omitted server-side when set: a
   * closure belongs to nobody, so it has no place on a patient's calendar.
   */
  leadId?: string;
}

/** Fetch the unified calendar feed for a date range. */
export const getCalendarFeedEvents = async (
  params: CalendarFeedParams
): Promise<CalendarFeedResponse> => {
  const search = new URLSearchParams();
  search.append('start', params.start);
  search.append('end', params.end);
  if (params.types?.length) {
    params.types.forEach((type) => search.append('types[]', type));
  }
  if (params.userId) {
    search.append('user_id', params.userId);
  }
  if (params.roomId) {
    search.append('room_id', params.roomId);
  }
  if (params.category) {
    search.append('category', params.category);
  }
  if (params.leadId) {
    search.append('lead_id', params.leadId);
  }
  const response = await api.get<CalendarFeedResponse>(`/calendar/events?${search}`);
  return response.data;
};
